import React, { useState, useEffect } from "react";
import { TextField, Button } from "@mui/material";
import { db } from "../../firebase";
import { collection, addDoc, Timestamp } from "firebase/firestore";
import { getAuth } from "firebase/auth";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";

const ContactForm: React.FC = () => {
  const auth = getAuth();
  const user = auth.currentUser;
  const { t } = useTranslation() as { t: (key: string) => string };

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    message: "",
  });

  const [success, setSuccess] = useState(false);

  // Auto-fill name & email from logged in user
  useEffect(() => {
    if (user) {
      setFormData({
        name: user.displayName || "",
        email: user.email || "",
        message: "",
      });
    }
  }, [user]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await addDoc(collection(db, "messages"), {
        name: formData.name,
        message: formData.message,
        email: user?.email || null,
        uid: user?.uid || null,
        createdAt: Timestamp.now(),
      });
      setSuccess(true);
      setFormData((prev) => ({
        ...prev,
        message: "",
      }));
    } catch (err) {
      console.error("Error sending message:", err);
    }
  };

  useEffect(() => {
    if (success) {
      const timeout = setTimeout(() => setSuccess(false), 3000);
      return () => clearTimeout(timeout);
    }
  }, [success]);

  return (
    <motion.form
      onSubmit={handleSubmit}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, ease: "easeOut" }}
      style={{
        maxWidth: 400,
        margin: "0 auto",
        display: "flex",
        flexDirection: "column",
        gap: "1rem",
      }}
    >
      <TextField
        name="name"
        label={t("form.name")}
        value={formData.name}
        onChange={handleChange}
        required
      />
      <TextField
        name="message"
        label={t("form.message")}
        multiline
        rows={4}
        value={formData.message}
        onChange={handleChange}
        required
      />
      <Button type="submit" variant="contained">
        {t("form.send")}
      </Button>
      <AnimatePresence>
        {success && (
          <motion.div
            key="success"
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.3 }}
            style={{
              textAlign: "center",
              color: "#4caf50",
              fontWeight: 500,
            }}
          >
            ✅ {t("form.success")}
          </motion.div>
        )}
      </AnimatePresence>
    </motion.form>
  );
};

export default ContactForm;
